import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { Scale, Building2, Hash, Loader2, ArrowRight } from 'lucide-react'
import { authApi, RegisterRequest } from '../services/api'
import { useOnboardingStatus } from '../hooks/useOnboardingStatus'

export default function OnboardingPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth0()
  const { exists, requiresPlanSelection, isLoading: statusLoading } = useOnboardingStatus()
  const [formData, setFormData] = useState<RegisterRequest>({
    organizationName: '',
    userName: '',
    nip: '',
    address: '',
  })

  // Redirect if user already exists
  useEffect(() => {
    if (!statusLoading && exists) {
      if (requiresPlanSelection) {
        navigate('/onboarding/select-plan', { replace: true })
      } else {
        // User is fully registered, go to main app
        navigate('/', { replace: true })
      }
    }
  }, [statusLoading, exists, requiresPlanSelection, navigate])

  // Pre-fill user name from Auth0 profile if available
  useEffect(() => {
    if (user?.name && !formData.userName) {
      setFormData(prev => ({ ...prev, userName: user.name || '' }))
    }
  }, [user])
  const [nipError, setNipError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: () => {
      // Invalidate the onboarding status to pick up the new registration
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
      // Navigate to plan selection
      navigate('/onboarding/select-plan', { replace: true })
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      const errorMessage =
        err.response?.data?.error ||
        'Wystapil blad podczas rejestracji. Sprobuj ponownie.'
      setError(errorMessage)
    },
  })

  /** Validate Polish NIP (10 digits with checksum) */
  const validateNip = (nip: string): string | null => {
    const digits = nip.replace(/[\s-]/g, '')
    if (!digits) return 'NIP jest wymagany'
    if (!/^\d{10}$/.test(digits)) return 'NIP musi skladac sie z 10 cyfr'
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += parseInt(digits[i]) * weights[i]
    }
    if (sum % 11 !== parseInt(digits[9])) {
      return 'Nieprawidlowy NIP - bledna suma kontrolna'
    }
    return null
  }

  const handleNipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setFormData((prev) => ({ ...prev, nip: value }))
    if (nipError) setNipError(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setNipError(null)

    if (!formData.organizationName.trim()) {
      setError('Nazwa organizacji jest wymagana')
      return
    }

    const nipValidationError = validateNip(formData.nip)
    if (nipValidationError) {
      setNipError(nipValidationError)
      return
    }

    // Normalize NIP to digits only before sending
    const normalizedData = {
      ...formData,
      nip: formData.nip.replace(/[\s-]/g, ''),
    }

    registerMutation.mutate(normalizedData)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Show loading while checking onboarding status
  if (statusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
              <Scale className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Witaj w LexTool!
            </h1>
            <p className="text-gray-500 mt-2">
              Skonfiguruj swoje konto, aby rozpoczac
            </p>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                1
              </div>
              <span className="text-sm font-medium text-gray-900">
                Dane organizacji
              </span>
            </div>
            <div className="w-8 h-px bg-gray-300" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 text-sm font-medium">
                2
              </div>
              <span className="text-sm text-gray-500">Wybor planu</span>
            </div>
          </div>

          {/* B2B notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-blue-800">
              LexTool jest narzedziem dla firm i kancelarii prawnych. Do rejestracji wymagany jest NIP.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Organization name - required */}
            <div>
              <label
                htmlFor="organizationName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nazwa kancelarii / firmy *
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="organizationName"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleChange}
                  placeholder="np. Kancelaria Kowalski & Partnerzy"
                  className="input pl-10 w-full"
                  required
                />
              </div>
            </div>

            {/* User name - optional */}
            <div>
              <label
                htmlFor="userName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Twoje imie i nazwisko
              </label>
              <input
                type="text"
                id="userName"
                name="userName"
                value={formData.userName}
                onChange={handleChange}
                placeholder="np. Jan Kowalski"
                className="input w-full"
              />
            </div>

            {/* NIP - required (B2B only) */}
            <div>
              <label
                htmlFor="nip"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                NIP firmy *
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="nip"
                  name="nip"
                  value={formData.nip}
                  onChange={handleNipChange}
                  placeholder="np. 123-456-78-90"
                  className={`input pl-10 w-full ${nipError ? 'border-red-400 focus:ring-red-400' : ''}`}
                  required
                />
              </div>
              {nipError ? (
                <p className="text-xs text-red-600 mt-1">{nipError}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  LexTool obsluguje wylacznie firmy - NIP jest wymagany
                </p>
              )}
            </div>

            {/* Address - optional */}
            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Adres (opcjonalnie)
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="np. ul. Prawnicza 15, 00-001 Warszawa"
                className="input w-full"
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={registerMutation.isPending}
              className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
            >
              {registerMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Tworzenie konta...
                </>
              ) : (
                <>
                  Dalej
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Note */}
          <p className="text-center text-xs text-gray-500 mt-6">
            Twoje dane sa bezpieczne i nie beda udostepniane osobom trzecim.
          </p>
        </div>
      </div>
    </div>
  )
}
